import { useToast } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { SmartFormProvider } from "@equidefi/portals/components/smart-form";
import { useUpdateUserById } from "@equidefi/portals/hooks/useUsers";
import type { DTO } from "@equidefi/shared/types";
import { Modal, isValidPhoneNumber } from "@equidefi/ui";

interface EditBasicInfoModalProps {
  modal: {
    isOpen: boolean;
    onClose: () => void;
  };
  user: DTO.User;
  onSuccess: () => void;
}

const schema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .regex(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, "Must be a valid email"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .refine((value) => isValidPhoneNumber(value), "Please enter a valid phone number"),
});

type EditBasicInfoFormValues = z.infer<typeof schema>;

export const EditBasicInfoModal = ({ modal, user, onSuccess }: EditBasicInfoModalProps) => {
  const toast = useToast();
  const updateUser = useUpdateUserById(user?.id);

  const form = useForm<EditBasicInfoFormValues>({
    defaultValues: {
      email: user?.email ?? "",
      phone: user?.phone ?? "",
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (modal.isOpen) {
      form.reset({ email: user?.email ?? "", phone: user?.phone ?? "" });
    }
  }, [modal.isOpen, form, user?.email, user?.phone]);

  const onSubmit = async (data: EditBasicInfoFormValues) => {
    try {
      await updateUser.mutateAsync(data);
      toast({
        status: "success",
        description: "User info updated successfully",
      });
      onSuccess();
      modal.onClose();
    } catch (error) {
      console.log(error);
      toast({
        status: "error",
        description: error.response?.data?.errors?.join(". ") || "Something went wrong",
      });
    }
  };

  return (
    <SmartFormProvider form={form}>
      {({ Input, PhoneField }) => (
        <Modal
          title="Edit User Info"
          isOpen={modal.isOpen}
          onClose={modal.onClose}
          onSubmit={form.handleSubmit(onSubmit)}
          buttons={[
            { label: "Save", type: "submit", isLoading: updateUser.isLoading },
            { label: "Dismiss", action: modal.onClose, variant: "outline" },
          ]}>
          <Input name="email" label="Email" />
          <PhoneField name="phone" label="Phone" isRequired />
        </Modal>
      )}
    </SmartFormProvider>
  );
};
